import React, { useState, useEffect } from 'react';
import { getUserBadges, getBadgesByCategory, getBadgeStats, ALL_BADGES, BADGE_CATEGORIES, BADGE_RARITIES } from '../services/badgeService';

function BadgeModal({ isOpen, onClose, userId }) {
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [badgesByCategory, setBadgesByCategory] = useState({});
  const [badgeStats, setBadgeStats] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen || !userId) return;

    const loadBadgeData = async () => {
      setLoading(true);
      try {
        const [badges, stats] = await Promise.all([
          getUserBadges(userId),
          getBadgeStats(userId)
        ]);

        setEarnedBadges(badges);
        setBadgesByCategory(getBadgesByCategory(badges));
        setBadgeStats(stats);
      } catch (error) {
        console.error('Error loading badge data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBadgeData();
  }, [isOpen, userId]);

  const getCategoryName = (category) => {
    switch (category) {
      case BADGE_CATEGORIES.ENGAGEMENT:
        return '🧠 Engagement';
      case BADGE_CATEGORIES.SOCIAL_REACTION:
        return '💬 Social Reactions';
      case BADGE_CATEGORIES.STREAK_CONSISTENCY:
        return '📈 Streak & Consistency';
      case BADGE_CATEGORIES.SOCIAL_MILESTONE:
        return '👥 Social Milestones';
      case BADGE_CATEGORIES.META_SYSTEM:
        return '🧪 Meta & System';
      default:
        return 'All Badges';
    }
  };

  const getFilteredBadges = () => {
    if (selectedCategory === 'all') {
      return earnedBadges;
    }
    return badgesByCategory[selectedCategory] || [];
  };

  const getAllBadgesInCategory = (category) => {
    if (category === 'all') {
      return Object.values(ALL_BADGES);
    }
    return Object.values(ALL_BADGES).filter(badge => badge.category === category);
  };

  if (!isOpen) return null;

  return (
    <div className="badge-modal-overlay" onClick={onClose}>
      <div className="badge-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="badge-modal-header">
          <h2>Badge Collection</h2>
          <button className="badge-modal-close" onClick={onClose}>×</button>
        </div>

        {loading ? (
          <div className="badge-modal-loading">
            <div className="emoji-loader">
              <span className="emoji-item">🏆</span>
              <span className="emoji-item">🎖️</span>
              <span className="emoji-item">🥇</span>
              <span className="emoji-item">⭐</span>
            </div>
            <p>Loading badges...</p>
          </div>
        ) : (
          <>
            {/* Badge Stats */}
            {badgeStats && (
              <div className="badge-stats-section">
                <div className="badge-stats-overview">
                  <div className="stat-item">
                    <div className="stat-number">{badgeStats.earnedCount}</div>
                    <div className="stat-label">Earned</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">{badgeStats.totalBadges}</div>
                    <div className="stat-label">Total</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">{badgeStats.completionPercentage}%</div>
                    <div className="stat-label">Complete</div>
                  </div>
                </div>
                
                <div className="completion-bar">
                  <div 
                    className="completion-fill"
                    style={{ width: `${badgeStats.completionPercentage}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Category Filters */}
            <div className="badge-category-filters">
              <button 
                className={`category-filter ${selectedCategory === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('all')}
              >
                All ({earnedBadges.length})
              </button>
              {Object.values(BADGE_CATEGORIES).map(category => {
                const categoryBadges = badgesByCategory[category] || [];
                const allInCategory = getAllBadgesInCategory(category);
                return (
                  <button 
                    key={category}
                    className={`category-filter ${selectedCategory === category ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {getCategoryName(category)} ({categoryBadges.length}/{allInCategory.length})
                  </button>
                );
              })}
            </div>

            {/* Badge Grid */}
            <div className="badge-grid-section">
              <div className="badge-grid">
                {getAllBadgesInCategory(selectedCategory).map(badge => {
                  const isEarned = earnedBadges.some(earned => earned.id === badge.id);
                  const earnedBadge = earnedBadges.find(earned => earned.id === badge.id);
                  const rarity = BADGE_RARITIES[badge.rarity] || BADGE_RARITIES.common;

                  return (
                    <div 
                      key={badge.id} 
                      className={`badge-card ${isEarned ? 'earned' : 'locked'} rarity-${badge.rarity}`}
                      style={{
                        borderColor: rarity.color,
                        boxShadow: isEarned && rarity.glow ? `0 0 10px ${rarity.color}40` : 'none'
                      }}
                    >
                      <div className="badge-icon-container">
                        <span className="badge-icon">{isEarned ? badge.icon : '🔒'}</span>
                        {badge.rarity === 'legendary' && isEarned && (
                          <div className="legendary-sparkles">✨</div>
                        )}
                        {badge.rarity === 'special' && isEarned && (
                          <div className="special-glow"></div>
                        )}
                      </div>
                      
                      <div className="badge-info">
                        <div className="badge-name">{badge.name}</div>
                        <div className="badge-description">{badge.description}</div>
                        
                        {isEarned && earnedBadge && (
                          <div className="badge-earned-date">
                            Earned {new Date(earnedBadge.earnedAt).toLocaleDateString()}
                          </div>
                        )}
                        
                        <div className={`badge-rarity rarity-${badge.rarity}`}>
                          {badge.rarity.charAt(0).toUpperCase() + badge.rarity.slice(1)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {getFilteredBadges().length === 0 && selectedCategory !== 'all' && (
                <div className="no-badges-message">
                  <div className="no-badges-icon">🎯</div>
                  <div className="no-badges-text">
                    No badges earned in this category yet.
                    <br />
                    Keep engaging to unlock them!
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default BadgeModal;